import {
  Button,
  FlexBox,
  Loader,
  space,
  Typography,
} from "@waldner-laboreinrichtungen/component-system";
import type { FC } from "react";
import DatePicker from "react-datepicker";
import { useTranslation } from "react-i18next";
import styledComponent from "styled-components";

import { ReactComponent as SuccessIcon } from "../../public/material-icons/check-circle-outline.svg";
import { ReactComponent as DownloadIcon } from "../../public/material-icons/file-download.svg";
import { ReactComponent as EyeIcon } from "../../public/material-icons/visibility-off.svg";

export type ExportStep =
  | "select"
  | "loading"
  | "ready"
  | "ready-usb"
  | "error"
  | "no-usb-connected"
  | "no-usb-detected";

export type ModalState = {
  step: ExportStep;
  startDate: Date;
  endDate: Date;
  downloadLink: string | null;
};

const StyledSeperator = styledComponent.div`
  border-top: ${({ theme }) =>
    `1px solid ${(theme as any).colorPalette.grey300}`};
  padding-top: ${space(2)};
`;

type ChannelExportContentProps = ModalState & {
  onClose: () => void;
  onAccept: () => void;
  onChange: (dates: [Date, Date]) => void;
  onDownload: (url: string) => void;
};

export const ChannelExportContent: FC<ChannelExportContentProps> = ({
  downloadLink,
  endDate,
  onAccept,
  onChange,
  onClose,
  onDownload,
  startDate,
  step,
}) => {
  const { t } = useTranslation();

  switch (step) {
    case "error": {
      return (
        <>
          <FlexBox paddingBottom={space(4)}>
            <Typography size="xl" weight="semiBold">
              {t("export.dataexport")}
            </Typography>
          </FlexBox>
          <StyledSeperator />
          <FlexBox
            alignItems="center"
            flexDirection="column"
            gap={space(2)}
            padding={`${space(20)} ${space(20)}`}
          >
            <Typography>{t("export.error")}</Typography>
          </FlexBox>
          <FlexBox gap={space(4)} justifyContent="end">
            <Button onClick={onClose} variant="secondary">
              {t("export.close")}
            </Button>
          </FlexBox>
        </>
      );
    }

    case "loading": {
      return (
        <>
          <FlexBox paddingBottom={space(4)}>
            <Typography size="xl" weight="semiBold">
              {t("export.dataexport")}
            </Typography>
          </FlexBox>
          <StyledSeperator />
          <FlexBox
            alignItems="center"
            flexDirection="column"
            gap={space(2)}
            padding={`${space(20)} ${space(20)}`}
          >
            <Loader />
            <Typography>{t("export.creating")}</Typography>
          </FlexBox>
        </>
      );
    }

    case "ready": {
      return (
        <>
          <FlexBox paddingBottom={space(4)}>
            <Typography size="xl" weight="semiBold">
              {t("export.dataexport")}
            </Typography>
          </FlexBox>
          <StyledSeperator />
          <FlexBox
            alignItems="center"
            flexDirection="column"
            gap={space(2)}
            padding={`${space(20)} ${space(20)}`}
          >
            <DownloadIcon fontSize={64} />
            <Typography>{t("export.ready")}</Typography>
          </FlexBox>
          <FlexBox gap={space(4)} justifyContent="end">
            <Button onClick={onClose} variant="secondary">
              {t("export.close")}
            </Button>
            {downloadLink && (
              <Button onClick={() => onDownload(downloadLink)}>
                {t("export.download")}
              </Button>
            )}
          </FlexBox>
        </>
      );
    }

    case "ready-usb": {
      return (
        <>
          <FlexBox paddingBottom={space(4)}>
            <Typography size="xl" weight="semiBold">
              {t("export.dataexport")}
            </Typography>
          </FlexBox>
          <StyledSeperator />
          <FlexBox
            alignItems="center"
            flexDirection="column"
            gap={space(2)}
            padding={`${space(20)} ${space(20)}`}
          >
            <SuccessIcon fontSize={64} />
            <Typography>{t("export.ready-usb")}</Typography>
            <Typography>{t("export.ready-usb-text")}</Typography>
          </FlexBox>
          <FlexBox gap={space(4)} justifyContent="end">
            <Button onClick={onClose} variant="secondary">
              {t("export.close")}
            </Button>
          </FlexBox>
        </>
      );
    }

    case "no-usb-connected": {
      return (
        <>
          <FlexBox paddingBottom={space(4)}>
            <Typography size="xl" weight="semiBold">
              {t("export.dataexport")}
            </Typography>
          </FlexBox>
          <StyledSeperator />
          <FlexBox
            alignItems="center"
            flexDirection="column"
            gap={space(2)}
            padding={`${space(20)} ${space(20)}`}
          >
            <EyeIcon fontSize={64} />
            <Typography>{t("export.no-usb")}</Typography>
            <Typography>{t("export.no-usb-text")}</Typography>
          </FlexBox>
          <FlexBox gap={space(4)} justifyContent="end">
            <Button onClick={onClose} variant="secondary">
              {t("export.close")}
            </Button>
            <Button onClick={onAccept}>{t("export.repeat")}</Button>
          </FlexBox>
        </>
      );
    }

    case "no-usb-detected": {
      return (
        <>
          <FlexBox paddingBottom={space(4)}>
            <Typography size="xl" weight="semiBold">
              {t("export.dataexport")}
            </Typography>
          </FlexBox>
          <StyledSeperator />
          <FlexBox
            alignItems="center"
            flexDirection="column"
            gap={space(2)}
            padding={`${space(20)} ${space(20)}`}
          >
            <EyeIcon fontSize={64} />
            <Typography>{t("export.no-usb-detected")}</Typography>
            <Typography>{t("export.no-usb-detected-text")}</Typography>
          </FlexBox>
          <FlexBox gap={space(4)} justifyContent="end">
            <Button onClick={onClose} variant="secondary">
              {t("export.close")}
            </Button>
            <Button onClick={onAccept}>{t("export.repeat")}</Button>
          </FlexBox>
        </>
      );
    }

    case "select": {
      return (
        <>
          <FlexBox paddingBottom={space(4)}>
            <Typography size="xl" weight="semiBold">
              {t("export.timeframe")}
            </Typography>
          </FlexBox>
          <StyledSeperator />
          <FlexBox
            alignItems="center"
            flexDirection="column"
            gap={space(2)}
            padding={`${space(20)} ${space(20)}`}
          >
            <DatePicker
              endDate={endDate}
              inline
              onChange={onChange}
              selected={startDate}
              selectsRange
              startDate={startDate}
            />
          </FlexBox>
          <FlexBox gap={space(4)} justifyContent="end">
            <Button onClick={onClose} variant="secondary">
              {t("export.close")}
            </Button>
            <Button onClick={onAccept}>{t("export.accept")}</Button>
          </FlexBox>
        </>
      );
    }
  }
};
